// Template import result modal for displaying CSV import results
// CHANGE: Removed all debug console statements for production
// Shows created templates with their configurations and errors
// CHANGE: Created for template CSV import feature

import { createPortal } from 'react-dom';
import { getPortalRoot } from '../../lib/portalUtils';
import { X, Download, CheckCircle, XCircle } from 'lucide-react';
import { useEffect } from 'react';
import { downloadTemplateResultsCSV } from '../../lib/csvImportTemplates';
import type { TemplateImportResult } from './TemplateImportModal';

interface TemplateImportResultModalProps {
  results: TemplateImportResult[];
  onClose: () => void;
  onImportComplete?: () => void;
}

export function TemplateImportResultModal({
  results,
  onClose,
  onImportComplete
}: TemplateImportResultModalProps) {
  const successfulTemplates = results.filter(r => !r.error);
  const failedTemplates = results.filter(r => r.error);

  const handleDownload = () => {
    downloadTemplateResultsCSV(results);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, onImportComplete]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    onClose();
    if (onImportComplete) {
      onImportComplete();
    }
  };

  return createPortal(
    <div
      className="modal-overlay-above"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="template-import-result-title"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 shrink-0">
          <div>
            <h3 id="template-import-result-title" className="text-xl font-bold text-gray-900">
              Résultats de l'import
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {successfulTemplates.length} template{successfulTemplates.length > 1 ? 's' : ''} importé{successfulTemplates.length > 1 ? 's' : ''}
              {failedTemplates.length > 0 && `, ${failedTemplates.length} erreur${failedTemplates.length > 1 ? 's' : ''}`}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Success Section */}
          {successfulTemplates.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle className="text-green-600" size={20} />
                  Templates importés avec succès
                </h4>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-all duration-200"
                >
                  <Download size={16} />
                  <span>Télécharger CSV</span>
                </button>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                          Nom du template
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                          Nombre d'items
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">
                          Récurrence
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {successfulTemplates.map((template, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                            {template.templateName}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{template.itemCount}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{template.recurrence}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Error Section */}
          {failedTemplates.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <XCircle className="text-red-600" size={20} />
                Erreurs rencontrées
              </h4>

              <div className="border border-red-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-red-50 border-b border-red-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-red-900 uppercase">
                          Nom du template
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-red-900 uppercase">
                          Nombre d'items
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-red-900 uppercase">
                          Erreur
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-100">
                      {failedTemplates.map((template, index) => (
                        <tr key={index} className="bg-white hover:bg-red-50">
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                            {template.templateName}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{template.itemCount}</td>
                          <td className="px-4 py-3 text-sm text-red-700">{template.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
          <button
            onClick={handleClose}
            className="w-full px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>,
    getPortalRoot()
  );
}
